import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

interface FullBlockingModalProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  innerClassName?: string;
  onBackdropClick?: React.MouseEventHandler<HTMLDivElement>;
  /** Optional accessible label for the dialog, when there's no visible title to point at. */
  ariaLabel?: string;
  /** Optional id of an element (typically the modal's own title) that labels the dialog. */
  ariaLabelledBy?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const FullBlockingModal: React.FC<FullBlockingModalProps> = ({
  children,
  className = '',
  innerClassName = '',
  onBackdropClick,
  ariaLabel,
  ariaLabelledBy,
  ...props
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Initial focus on open: focus the first focusable element inside the
  // dialog if there is one, otherwise the dialog container itself, so
  // keyboard/screen-reader users land inside the modal instead of wherever
  // focus happened to be on the page underneath it.
  useEffect(() => {
    const container = dialogRef.current;
    if (!container) return;

    const focusable = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable ?? container).focus();
  }, []);

  // Escape-to-close: reuses onBackdropClick as the existing "onClose-style"
  // prop (several callers already pass onBackdropClick={onClose}), so this
  // is a no-op for consumers that don't pass it. Note: this intentionally
  // does not implement a full focus trap/focus-restoration — that's a
  // larger change left for a follow-up; this only covers initial focus,
  // role/aria-modal, and Escape.
  useEffect(() => {
    if (!onBackdropClick) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onBackdropClick(event as unknown as React.MouseEvent<HTMLDivElement>);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onBackdropClick]);

  const modal = (
    <div
      className={twMerge(
        `fixed inset-0 z-50 flex items-center justify-center bg-black/30`,
        className
      )}
      {...props}
      onClick={e => {
        if (e.target === e.currentTarget && onBackdropClick) {
          onBackdropClick(e);
        }
        e.stopPropagation();
        if (props.onClick) props.onClick(e);
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        className={twMerge(
          `bg-white rounded-2xl p-4 md:p-6 w-11/12 max-w-sm relative overflow-y-auto max-h-[90vh] md:max-w-md`,
          innerClassName
        )}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default FullBlockingModal;
